import React, { useEffect, useMemo, useState } from 'react';
import { Button, SimpleGrid, Stack, useToast } from '@chakra-ui/react';
import { ArrowLeft, Save, SendHorizonal } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { createQuote } from '../../../hooks/customer_quotes';
import { getCatalogueItems } from '../../../hooks/catalogueItems';
import { fetchLeads } from '../../../hooks/leads';
import { createQuoteItem } from '../../../hooks/quote_items';
import { Lead, QuoteItem } from '../../../utils/types';
import { LeadSelector } from '../components/LeadSelector';
import {
  QuoteHero,
  QuotePrimaryButton,
  QuoteSectionCard,
  QuoteShell,
} from '../components/quoteUi';
import {
  QuoteLineItemsSection,
  QuoteNotesSection,
  QuoteRecipientSection,
  QuoteSummaryRail,
  generateQuoteReference,
} from '../components/quoteBuilderShared';

const normalizeLeads = (data: unknown): Lead[] => {
  if (Array.isArray(data)) {
    return data as Lead[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Lead[] }).results;
  }

  return [];
};

const normalizeProducts = (data: unknown) => {
  if (Array.isArray(data)) {
    return data as Array<{ id: string; name: string; price?: number }>;
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Array<{ id: string; name: string; price?: number }> }).results;
  }

  return [];
};

const createDraftItem = (): QuoteItem => ({
  id: Date.now(),
  item_name: '',
  quantity: 1,
  unit_price: 0,
  total_price: 0,
});

export default function NewQuotePage() {
  const { orgId, catalogueId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [referenceNumber] = useState(generateQuoteReference);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [products, setProducts] = useState<Array<{ id: string; name: string; price?: number }>>([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [notes, setNotes] = useState('Looking forward to your business.');
  const [validUntil, setValidUntil] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [leadRes, productRes] = await Promise.all([
          fetchLeads(orgId || ''),
          getCatalogueItems(orgId || '', catalogueId || ''),
        ]);

        if (!mounted) {
          return;
        }

        if (leadRes?.success) {
          setLeads(normalizeLeads(leadRes.data));
        }

        if (productRes?.success) {
          setProducts(normalizeProducts(productRes.data));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [orgId, catalogueId]);

  const selectedLead = useMemo(
    () => leads.find((lead) => String(lead.id) === String(selectedLeadId)),
    [leads, selectedLeadId],
  );

  const handleAddItem = () => {
    setItems((current) => [...current, createDraftItem()]);
  };

  const handleDuplicateItem = (index: number) => {
    setItems((current) => {
      const item = current[index];
      if (!item) {
        return current;
      }
      const duplicate = {
        ...item,
        id: Date.now(),
      };
      const next = [...current];
      next.splice(index + 1, 0, duplicate);
      return next;
    });
  };

  const handleChangeItem = (
    index: number,
    field: keyof QuoteItem,
    value: string | number,
  ) => {
    setItems((current) => {
      const next = [...current];
      const item = next[index];
      if (!item) {
        return current;
      }

      const updated = { ...item, [field]: value };

      if (field === 'item_name') {
        const selectedProduct = products.find((product) => product.name === value);
        updated.unit_price = Number(selectedProduct?.price || 0);
      }

      updated.total_price = Number(updated.quantity || 0) * Number(updated.unit_price || 0);
      next[index] = updated;
      return next;
    });
  };

  const handleRemoveItem = (index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleCreate = async () => {
    if (!orgId || !selectedLeadId || !items.length) {
      toast({
        title: 'Finish the core quote details',
        description: 'Select a recipient and add at least one line item before saving.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await createQuote(
        orgId,
        selectedLeadId,
        referenceNumber,
        notes,
        validUntil || null,
        null,
      );

      if (!response.success) {
        throw new Error('Quote creation failed');
      }

      const quoteId = response.data.id;

      for (const item of items) {
        await createQuoteItem(
          orgId,
          quoteId,
          item.item_name,
          item.quantity,
          item.unit_price,
        );
      }

      toast({
        title: 'Quotation created',
        description: `${referenceNumber} has been saved successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate(`/organization/${orgId}/quotes/view/${quoteId}`);
    } catch (error) {
      toast({
        title: 'Could not create quotation',
        description: 'Please review the data and try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <QuoteShell compact>
        <QuoteSectionCard
          title="Loading quote builder"
          description="Please wait while quote recipients and catalogue items are loading."
        >
          <Button variant="ghost" isLoading loadingText="Loading" />
        </QuoteSectionCard>
      </QuoteShell>
    );
  }

  return (
    <QuoteShell compact>
      <QuoteHero
        eyebrow="New quotation"
        title="Build a quotation with better recipient context and faster pricing confidence"
        description="Start with the right customer, add line items cleanly, and keep the total visible while you build."
        primaryAction={
          <QuotePrimaryButton
            leftIcon={<Save size={16} />}
            onClick={handleCreate}
            isLoading={submitting}
            loadingText="Saving"
          >
            Save draft
          </QuotePrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <QuoteRecipientSection
            selectedLeadId={selectedLeadId}
            selectedLead={selectedLead}
            selector={
              <LeadSelector
                orgId={orgId || ''}
                selectedLead={selectedLeadId}
                onSelect={setSelectedLeadId}
                onAddNew={() => navigate(`/organization/${orgId}/leads/new`)}
              />
            }
          />

          <QuoteLineItemsSection
            items={items}
            products={products}
            onAddItem={handleAddItem}
            onDuplicateItem={handleDuplicateItem}
            onChangeItem={handleChangeItem}
            onRemoveItem={handleRemoveItem}
          />

          <QuoteNotesSection
            notes={notes}
            validUntil={validUntil}
            onChangeNotes={setNotes}
            onChangeValidUntil={setValidUntil}
          />
        </Stack>

        <Stack spacing={6}>
          <QuoteSummaryRail
            referenceNumber={referenceNumber}
            status="draft"
            items={items}
            notes={notes}
            validUntil={validUntil}
          />
          <QuoteSectionCard
            title="Actions"
            description="Keep the primary quote actions stable while you build."
          >
            <Stack spacing={3}>
              <QuotePrimaryButton
                leftIcon={<Save size={16} />}
                onClick={handleCreate}
                isLoading={submitting}
                loadingText="Saving"
              >
                Save draft
              </QuotePrimaryButton>
              <Button borderRadius="16px" variant="outline" leftIcon={<SendHorizonal size={16} />} isDisabled>
                Share after save
              </Button>
            </Stack>
          </QuoteSectionCard>
        </Stack>
      </SimpleGrid>
    </QuoteShell>
  );
}
